import { FC, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { DateTime } from "luxon";
import { AnimatePresence } from "framer-motion";

import Text from "@/components/Text";
import DistributionCalendarModal from "./DistributionCalendarModal";
import { ChannelResource, ReleaseResource } from "@/types/resources";
import DistributionScheduleModal from "./DistributionScheduleModal";

const ScheduleButton = styled.button`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscale};
  height: 18px;
  width: 100%;
  display: none;
`;

const Card = styled.div`
  height: 175px;
  background-color: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: 4px;
  display: flex;
  align-content: space-between;
  flex-wrap: wrap;
  &:nth-child(-n + 7) {
    border-top-color: transparent;
  }
  &:nth-child(7n) {
    border-right-color: transparent;
  }
  &:first-child {
    border-top-left-radius: 4px;
  }
  &:last-child {
    border-bottom-right-radius: 4px;
  }
  &:nth-child(7) {
    border-top-right-radius: 4px;
  }
  &:nth-last-child(7) {
    border-bottom-left-radius: 4px;
  }
  &:hover {
    ${ScheduleButton} {
      display: block;
    }
  }
`;

interface DayProps {
  $isToday?: boolean;
  $isOtherMonthDay?: boolean;
}

const Day = styled.div<DayProps>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-left: 8px;
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $isOtherMonthDay }) =>
    $isOtherMonthDay &&
    css`
      ${Text} {
        color: ${({ theme }) => theme.colors.gray};
      }
    `}
  ${({ $isToday }) =>
    $isToday &&
    css`
      background: ${({ theme }) => theme.colors.error};
      ${Text} {
        color: ${({ theme }) => theme.colors.white};
      }
    `}
`;

const CardBottom = styled.div`
  width: 100%;
`;

interface SchedulesProps {
  $isOtherMonthDay?: boolean;
}

// const TimeBox = styled.div<SchedulesProps>`
//   background: ${({ theme }) => theme.colors.grayscale};
//   border-radius: 4px;
//   padding: 8px;
//   display: flex;
//   color: ${({ theme }) => theme.colors.purpleLighter};
//   margin-top: 4px;
//   svg {
//     flex-shrink: 0;
//     margin-right: 3px;
//     transform: translateY(-1px);
//   }
//   ${({ $isOtherMonthDay }) =>
//     $isOtherMonthDay &&
//     css`
//       opacity: 0.5;
//     `}
// `;

const Schedules = styled.div<SchedulesProps>`
  padding: 8px;
  background: ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  margin-top: 4px;
  position: relative;
  cursor: pointer;
  ${({ $isOtherMonthDay }) =>
    $isOtherMonthDay &&
    css`
      opacity: 0.5;
    `}
`;

const SchedulesBadge = styled.div`
  display: inline-block;
  padding: 2px;
  background: ${({ theme }) => theme.colors.purple};
  border-radius: 2px;
  min-width: 16px;
  text-align: center;
  margin-right: 6px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const SchedulesTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const StyledText = styled(Text)`
  font-size: 11px;
`;

const ScheduleCircles = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 4px;
`;

const ScheduleCircle = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  margin-right: -4px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: ${({ theme }) => theme.colors.warning};
  &:nth-last-child(5) {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    z-index: 5;
  }
`;

const SchedulesHolder = styled.div`
  position: relative;
`;

interface Props {
  isOtherMonthDay: boolean;
  isToday?: boolean;
  day: DateTime;
  data?: ReleaseResource[];
  isDayBeforeToday: boolean;
}

const DistributionCalendarCard: FC<Props> = ({
  isToday,
  day,
  isOtherMonthDay,
  data,
  isDayBeforeToday,
}) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [showCardModal, setShowCardModal] = useState(false);

  const [userClickCoordinates, setUserClickCoordinates] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  const dataId = "card_" + day.toFormat("yyyyLLdd");

  const distinctChannels = useMemo(() => {
    if (!data) {
      return [];
    }

    const obj: {
      [channelId: number]: Pick<
        ChannelResource,
        "name" | "id" | "thumbnail" | "network" | "tags"
      >;
    } = {};

    data.forEach((release) => {
      if (!release.channel) {
        return;
      }
      obj[release.channel.id] = release.channel;
    });

    return Object.values(obj);
  }, [data]);

  return (
    <Card>
      <Day $isToday={isToday} $isOtherMonthDay={isOtherMonthDay}>
        <Text $type="extraSmall" $color={isToday ? "white" : "black"}>
          {day.toFormat("d")}
        </Text>
      </Day>
      <CardBottom>
        {!isDayBeforeToday && (
          <ScheduleButton onClick={() => setShowScheduleModal(true)}>
            <svg
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="6"
                height="6"
                transform="translate(1 1)"
                fill="transparent"
              />
              <path
                d="M4 1V7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1 4H7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ScheduleButton>
        )}
        {!!data?.length && (
          <SchedulesHolder>
            <Schedules
              data-id={dataId}
              onClick={(e) => {
                setUserClickCoordinates({ x: e.clientX, y: e.clientY });
                setShowCardModal((prev) => !prev);
              }}
              $isOtherMonthDay={isOtherMonthDay}
            >
              <SchedulesTitle data-id={dataId}>
                <SchedulesBadge data-id={dataId}>
                  <Text $type="extraSmall" $color="white" data-id={dataId}>
                    {data.length}
                  </Text>
                </SchedulesBadge>
                <StyledText $type="smallBold" data-id={dataId}>
                  Schedules
                </StyledText>
              </SchedulesTitle>
              <ScheduleCircles data-id={dataId}>
                {distinctChannels.length > 4 && (
                  <ScheduleCircle data-id={dataId}>
                    <Text $type="superSmall" data-id={dataId}>
                      +{distinctChannels.length - 4}
                    </Text>
                  </ScheduleCircle>
                )}
                {distinctChannels.slice(0, 4).map((channel) => (
                  <ScheduleCircle key={channel.id} data-id={dataId}>
                    {!!channel?.thumbnail && (
                      <Image
                        data-id={dataId}
                        src={channel.thumbnail}
                        alt={channel.name + " thumbnail"}
                      />
                    )}
                  </ScheduleCircle>
                ))}
              </ScheduleCircles>
            </Schedules>
            {showCardModal && (
              <DistributionCalendarModal
                onOpenScheduleModal={() => setShowScheduleModal(true)}
                day={day}
                data={data}
                coordinates={userClickCoordinates}
                onClose={() => setShowCardModal(false)}
                isDayBeforeToday={isDayBeforeToday}
              />
            )}
          </SchedulesHolder>
        )}
        {/* TODO: uncomment when ready */}
        {/* {!isDayBeforeToday && data?.timeAvailable && (
          <TimeBox $isOtherMonthDay={isOtherMonthDay}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.50014 13.0714C10.5772 13.0714 13.0716 10.577 13.0716 7.5C13.0716 4.42299 10.5772 1.92857 7.50014 1.92857C4.42312 1.92857 1.92871 4.42299 1.92871 7.5C1.92871 10.577 4.42312 13.0714 7.50014 13.0714Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
              />
              <path
                d="M7.30005 4.5V7.75H10.55"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Text $type="extraSmall" $color="purple">
              Suggested time available
            </Text>
          </TimeBox>
        )} */}
      </CardBottom>
      <AnimatePresence>
        {showScheduleModal && (
          <DistributionScheduleModal
            onClose={() => setShowScheduleModal(false)}
            day={day}
          />
        )}
      </AnimatePresence>
    </Card>
  );
};

export default DistributionCalendarCard;
